import { useEffect, useState } from 'react';

/**
 * Fetches an image URL once and inlines it as a data: URI, deduped per URL across the
 * whole app (module-level cache, not per-component).
 *
 * Why this exists: a My Images URL (e.g. a QR code's logo or fallback destination image)
 * can be referenced by several things on the same page at once -- a plain <img> thumbnail,
 * QRCodePreview's live SVG embed (which needs crossOrigin so qr-code-styling can export the
 * canvas for .download()), and potentially the same image reused for both a QR's logo and
 * its fallback destination. Several independent requests for the identical URL landing close
 * together -- some in plain mode, some in crossOrigin mode -- reliably trips a long-standing
 * Chromium bug (marked WontFix upstream: the disk cache doesn't distinguish a CORS-mode
 * response from a non-CORS one for the same URL), intermittently failing whichever request
 * loses the race with a CORS error even though the server's CORS headers are correct.
 * Confirmed via Chrome DevTools Protocol network inspection, in both dev and production
 * builds. See issue #70 for the write-up and sources.
 *
 * Deduping our own fetches (this cache) closes the common case, but doesn't protect against
 * something outside our control also touching the bare URL in a different mode -- e.g. a
 * third-party library's own internal image loading. Appending a stable, distinguishing query
 * parameter to *our* request gives it its own cache key that can never collide with a
 * plain-mode request for the bare URL, regardless of who else touches it -- the standard
 * mitigation for this specific Chromium bug. It doesn't defeat caching: the marker is fixed,
 * not random, so repeat calls for the same original URL still hit one warm cache entry.
 *
 * Once we hold the result as a data: URI, nothing downstream -- not a thumbnail <img>, not
 * qr-code-styling -- ever issues a network request for the original URL again.
 */
const dataUrlCache = new Map<string, Promise<string | null>>();

function withCorsCacheBuster(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}cors=1`;
}

export function loadImageAsDataUrlOnce(url: string): Promise<string | null> {
  let cached = dataUrlCache.get(url);
  if (!cached) {
    cached = fetch(withCorsCacheBuster(url), { mode: 'cors' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          })
      )
      .catch(() => null);
    dataUrlCache.set(url, cached);
  }
  return cached;
}

/** React hook wrapper around loadImageAsDataUrlOnce, for rendering a thumbnail <img>. */
export function useImageDataUrl(url: string | undefined | null): string | undefined {
  const [dataUrl, setDataUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!url) {
      setDataUrl(undefined);
      return;
    }
    let cancelled = false;
    loadImageAsDataUrlOnce(url).then((resolved) => {
      if (!cancelled) setDataUrl(resolved || undefined);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return dataUrl;
}
